import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { extractErrorMessage } from '../api/errorUtils';
import kitchenService from '../services/kitchenService';
import { showNotification } from './notificationSlice';

// ─── Async Thunks ──────────────────────────────────────────────────────────────

export const fetchKitchenFoods = createAsyncThunk(
  'kitchen/fetchFoods',
  async (params = {}, { rejectWithValue }) => {
    try {
      return await kitchenService.listKitchenFoods(params);
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const fetchFactors = createAsyncThunk(
  'kitchen/fetchFactors',
  async (params = {}, { rejectWithValue }) => {
    try {
      return await kitchenService.listFactors(params);
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const createKitchenFood = createAsyncThunk(
  'kitchen/createFood',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const data = await kitchenService.createKitchenFood(payload);
      if (data?.success === false) {
        dispatch(showNotification({ message: data.message, severity: 'error', title: 'Failed to create kitchen food' }));
        return rejectWithValue(data.message);
      }
      dispatch(showNotification({ message: 'Kitchen food created', severity: 'success', title: 'Success' }));
      return data;
    } catch (error) {
      const message = extractErrorMessage(error);
      dispatch(showNotification({ message, severity: 'error', title: 'Failed to create kitchen food' }));
      return rejectWithValue(message);
    }
  }
);

export const createFactor = createAsyncThunk(
  'kitchen/createFactor',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const data = await kitchenService.createFactor(payload);
      if (data?.success === false) {
        dispatch(showNotification({ message: data.message, severity: 'error', title: 'Failed to create ingredient' }));
        return rejectWithValue(data.message);
      }
      dispatch(showNotification({ message: 'Ingredient created', severity: 'success', title: 'Success' }));
      return data;
    } catch (error) {
      const message = extractErrorMessage(error);
      dispatch(showNotification({ message, severity: 'error', title: 'Failed to create ingredient' }));
      return rejectWithValue(message);
    }
  }
);

export const updateFactor = createAsyncThunk(
  'kitchen/updateFactor',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const data = await kitchenService.updateFactor(payload);
      if (data?.success === false) {
        dispatch(showNotification({ message: data.message, severity: 'error', title: 'Failed to update ingredient' }));
        return rejectWithValue(data.message);
      }
      dispatch(showNotification({ message: 'Ingredient updated', severity: 'success', title: 'Success' }));
      return data;
    } catch (error) {
      const message = extractErrorMessage(error);
      dispatch(showNotification({ message, severity: 'error', title: 'Failed to update ingredient' }));
      return rejectWithValue(message);
    }
  }
);

export const fetchProductionMonitor = createAsyncThunk(
  'kitchen/monitor',
  async (params = {}, { rejectWithValue }) => {
    try {
      return await kitchenService.getProductionMonitor(params);
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const fetchUoms = createAsyncThunk(
  'kitchen/fetchUoms',
  async (_, { rejectWithValue }) => {
    try {
      return await kitchenService.getUoms();
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

// ─── Slice ─────────────────────────────────────────────────────────────────────

const initialState = {
  foods: [],
  foodsTotal: 0,
  factors: [],
  factorsTotal: 0,
  monitor: [],
  uoms: [],
  isLoadingFoods: false,
  isLoadingFactors: false,
  isLoadingMonitor: false,
  isSaving: false,
  error: null,
};

const kitchenSlice = createSlice({
  name: 'kitchen',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchKitchenFoods.pending, (state) => { state.isLoadingFoods = true; state.error = null; })
      .addCase(fetchKitchenFoods.fulfilled, (state, action) => {
        state.isLoadingFoods = false;
        state.foods = action.payload?.foods || [];
        state.foodsTotal = action.payload?.total || 0;
      })
      .addCase(fetchKitchenFoods.rejected, (state, action) => { state.isLoadingFoods = false; state.error = action.payload; })

      .addCase(fetchFactors.pending, (state) => { state.isLoadingFactors = true; state.error = null; })
      .addCase(fetchFactors.fulfilled, (state, action) => {
        state.isLoadingFactors = false;
        state.factors = action.payload?.factors || [];
        state.factorsTotal = action.payload?.total || 0;
      })
      .addCase(fetchFactors.rejected, (state, action) => { state.isLoadingFactors = false; state.error = action.payload; })

      .addCase(createKitchenFood.pending, (state) => { state.isSaving = true; })
      .addCase(createKitchenFood.fulfilled, (state) => { state.isSaving = false; })
      .addCase(createKitchenFood.rejected, (state) => { state.isSaving = false; })

      .addCase(createFactor.pending, (state) => { state.isSaving = true; })
      .addCase(createFactor.fulfilled, (state) => { state.isSaving = false; })
      .addCase(createFactor.rejected, (state) => { state.isSaving = false; })
      .addCase(updateFactor.pending, (state) => { state.isSaving = true; })
      .addCase(updateFactor.fulfilled, (state) => { state.isSaving = false; })
      .addCase(updateFactor.rejected, (state) => { state.isSaving = false; })

      .addCase(fetchProductionMonitor.pending, (state) => { state.isLoadingMonitor = true; })
      .addCase(fetchProductionMonitor.fulfilled, (state, action) => {
        state.isLoadingMonitor = false;
        state.monitor = action.payload || [];
      })
      .addCase(fetchProductionMonitor.rejected, (state) => { state.isLoadingMonitor = false; })

      .addCase(fetchUoms.fulfilled, (state, action) => { state.uoms = action.payload || []; });
  },
});

export default kitchenSlice.reducer;
